import { useState } from 'react';
import { PawPrint } from 'lucide-react';

import { useAuth } from '../../../auth/AuthProvider.jsx';
import { authApi } from '../../../api/auth.js';
import { ApiError } from '../../../api/client.js';
import { env } from '../../../lib/env.js';

/**
 * Forced first-password screen. Shown to an owner whose account was provisioned
 * with a temporary password (kiosk self-registration, `mustChangePassword`).
 * They enter the temporary password received by email and choose their own. On
 * success the obligation is cleared server-side and the portal re-hydrates.
 */
export function FirstPasswordPage() {
  const { refresh } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const policyOk = password.length >= 12 && /[a-z]/.test(password) && /[A-Z]/.test(password) && /\d/.test(password);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!currentPassword) { setError('Inserisci la password temporanea ricevuta via email.'); return; }
    if (!policyOk) { setError('La nuova password deve avere almeno 12 caratteri, con maiuscola, minuscola e numero.'); return; }
    if (password !== confirm) { setError('Le password non coincidono.'); return; }
    setSubmitting(true);
    try {
      await authApi.changePassword({ currentPassword, newPassword: password });
      await refresh();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Qualcosa è andato storto. Riprova.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-portal-bg px-6 text-center">
      <div className="w-full max-w-sm">
        <p className="text-xs font-semibold uppercase tracking-wider text-portal-ink-faint">{env.appName}</p>
        <PawPrint className="mx-auto mt-6 h-12 w-12 text-portal-brand" strokeWidth={1.5} />
        <h1 className="mt-4 text-2xl font-semibold text-portal-ink">Scegli la tua password</h1>
        <p className="mt-2 text-portal-ink-muted">
          Per la tua sicurezza, sostituisci la password temporanea con una scelta da te.
        </p>

        <form onSubmit={submit} className="mt-6 space-y-3 text-left">
          <div>
            <label className="text-sm font-medium text-portal-ink">Password temporanea</label>
            <input
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              autoComplete="current-password"
              placeholder="Quella ricevuta via email"
              className="mt-1 w-full rounded-portal border border-portal-border bg-portal-card px-4 py-3 text-portal-ink outline-none focus:border-portal-brand"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-portal-ink">Nuova password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              placeholder="Almeno 12 caratteri"
              className="mt-1 w-full rounded-portal border border-portal-border bg-portal-card px-4 py-3 text-portal-ink outline-none focus:border-portal-brand"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-portal-ink">Conferma nuova password</label>
            <input
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
              className="mt-1 w-full rounded-portal border border-portal-border bg-portal-card px-4 py-3 text-portal-ink outline-none focus:border-portal-brand"
            />
          </div>

          {error && <p className="text-sm text-portal-brand">{error}</p>}

          <button
            type="submit"
            disabled={submitting || !currentPassword || !password || !confirm}
            className="w-full rounded-full bg-portal-brand px-6 py-3 font-medium text-white hover:bg-portal-brand-hover disabled:opacity-50"
          >
            {submitting ? 'Salvo…' : 'Imposta password'}
          </button>
        </form>
      </div>
    </div>
  );
}
